import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Loader from "../../../components/Loader/Loader";
import DecisionForm from "../../../components/dr/DecisionForm";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Btn from "../../../components/v3/Btn";
import ApprovalContextCards, { investorName, personName, KeyVal } from "../../DR/Approvals/ApprovalContextCards";
import SuitabilityAnswersCard, { answersMatchItem } from "../../../components/dr/SuitabilityAnswersCard";
import { drApi } from "../../../services/drApi";
import { useRole } from "../../../hooks/useRole";
import useSuitabilityAnswers from "../../../hooks/useSuitabilityAnswers";
import { needsReviewedRevision } from "../../../utils/suitabilityRevision";
import {
  apiErrorCode,
  apiErrorMessage,
  isSubmissionSuperseded,
  submissionSupersededMessage,
} from "../../../utils/apiError";
import { sendNamingAccountOnce } from "../../../utils/decisionAccount";

/**
 * ComplianceDecisionDetail - review or reverse one recorded decision.
 *
 * The two actions are deliberately separate forms rather than one form with
 * four outcomes. Reviewing says "I have looked at this"; reversing overrides a
 * colleague's regulated judgement and notifies the investor. Collapsing them
 * into one control would make the second reachable by a slip of the mouse.
 *
 * A Platform Manager reaches this screen for oversight and sees both forms
 * disabled: reading a decision is not reviewing it.
 */

const REVIEW_OPTIONS = [
  { value: "REVIEWED", label: "Reviewed", tone: "ok" },
  { value: "FLAGGED", label: "Flagged", tone: "danger" },
];

// A reversal can land on any outcome a decision can, NEEDS_INFO included:
// "the original approval was wrong and we need more from the investor" is a
// real correction, and omitting it forced a reviewer to reject instead.
//
// EXCEPT on a CLASSIFICATION. There, NEEDS_INFO leaves the classification,
// qualification and accreditation sitting APPROVED while the console reports
// the reversal succeeded: the reviewer believes they have withdrawn an
// accreditation they have not. The API refuses the combination now, and this
// does not offer it in the first place.
const REVERSE_APPROVE_REJECT = [
  { value: "APPROVED", label: "Approve", tone: "ok" },
  { value: "REJECTED", label: "Reject", tone: "danger" },
];
const REVERSE_NEEDS_INFO = {
  value: "NEEDS_INFO",
  label: "Request more information",
  tone: "neutral",
};

function reverseOptions(submissionType) {
  return String(submissionType || "").toUpperCase() === "CLASSIFICATION"
    ? REVERSE_APPROVE_REJECT
    : [...REVERSE_APPROVE_REJECT, REVERSE_NEEDS_INFO];
}

const CODE_COPY = {
  self_review: "You cannot review or reverse your own decision.",
  same_decision: "The decision already has this outcome.",
  not_latest_decision:
    "A newer decision exists for this investor and product. Open the latest one.",
  not_decided: "This record has no decision to review yet.",
  needs_info_not_supported_for_classification:
    "Requesting more information is not available for classification decisions; approve or reject instead.",
  // Person-first WP6c: the account refusals of a reversal (api WP5
  // complianceDecision.decisionAccountOf). Each is thrown before any write,
  // so nothing was reversed; the API sends the code alone.
  ACCOUNT_KIND_REQUIRED:
    "This investor has a personal and a company account, and the server could not tell which one this decision is for. Nothing was reversed.",
  ACCOUNT_SELECTOR_CONFLICT:
    "The account chosen is not the account this decision is about. Nothing was reversed.",
  ACCOUNT_NOT_SELECTABLE:
    "The investor account this decision is about is closed, so it cannot be reversed. Nothing was reversed.",
  CORPORATE_ACCOUNT_NOT_SET_UP:
    "This investor has not set up a company account, so there is no company to reverse this decision for. Nothing was reversed.",
  INVESTOR_ACCOUNT_NOT_FOUND: "The investor account was not found. Nothing was reversed.",
};
const ACCOUNT_NOT_CHOSEN = "Nothing was reversed. Choose Personal or Company to reverse this decision.";

function reviewerLabel(r) {
  return personName(r?.Reviewer || r?.reviewer, r?.reviewer_user_id);
}

function ComplianceDecisionDetail() {
  const { slaId } = useParams();
  const navigate = useNavigate();
  const { canReview } = useRole();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [reverseMessage, setReverseMessage] = useState("");

  // API round 15 (Diego, 28 Oct): reversing a SUITABILITY decision about an
  // order is decided on the answers that decision was about. They are read and
  // shown here; the reversal is offered only while the answers on screen
  // belong to the decision's submission, and it carries the revision shown. A
  // review writes no order and is not bound.
  //
  // Codex r14 P1: the page belongs to the decision the route names. It is
  // remounted per slaId (ComplianceDecisionDetailRoute below), its own
  // refetches are sequenced, and a reversal is offered only for a payload
  // whose id is the route's, with answers read for that decision's own
  // investment (answersMatchItem).
  const slaRow = payload?.sla;
  const routeItem = !!slaRow && String(slaRow.id) === String(slaId);
  const bindsAnswers = routeItem && needsReviewedRevision(slaRow);
  const answers = useSuitabilityAnswers(bindsAnswers ? slaRow.investment_id : null);
  const reversalBound = routeItem && (!bindsAnswers || answersMatchItem(answers, slaRow));
  const detailSeq = useRef(0);

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slaId]);

  async function fetchDetail() {
    const mine = ++detailSeq.current;
    setLoading(true);
    try {
      const res = await drApi.approvalDetail(slaId);
      if (mine !== detailSeq.current) return;
      setPayload(res?.data || null);
      setError(null);
    } catch (err) {
      if (mine !== detailSeq.current) return;
      setError(apiErrorMessage(err, "Fetch failed"));
    } finally {
      if (mine === detailSeq.current) setLoading(false);
    }
  }

  // Both submit handlers rethrow with mapped copy so DecisionForm renders the
  // refusal inline, beside the control that produced it.
  function rethrow(err) {
    // A reversal of a decision about an earlier submission: the API's sentence
    // says what happened, so it wins over a fixed line.
    if (isSubmissionSuperseded(err)) throw new Error(submissionSupersededMessage(err));
    const copy = CODE_COPY[apiErrorCode(err)];
    throw copy ? new Error(copy) : err;
  }

  async function submitReview({ decision, notes }) {
    try {
      await drApi.reviewDecision(slaId, { outcome: decision, notes });
      setFlash({ tone: "info", text: "Review recorded." });
      await fetchDetail();
    } catch (err) {
      rethrow(err);
    }
  }

  async function submitReverse({ decision, notes }) {
    if (!reversalBound) throw new Error("The answers on this page do not belong to this decision. Reload the page.");
    try {
      const body = { decision, reason: notes };
      const message = reverseMessage.trim();
      if (message) body.investor_message = message;
      if (bindsAnswers) body.reviewed_submission_rev = answers.submissionRev;
      // Person-first WP6c: the body names no account. Only when the server
      // answers 400 ACCOUNT_KIND_REQUIRED (a legacy CLASSIFICATION original,
      // two accounts) is the reviewer asked Personal or Company, and the same
      // body sent once more with account_kind. A cancel sends nothing more.
      const sent = await sendNamingAccountOnce((b) => drApi.reverseDecision(slaId, b), body);
      if (sent.cancelled) throw new Error(ACCOUNT_NOT_CHOSEN);
      setFlash({ tone: "info", text: "Decision reversed. The investor has been notified." });
      setReverseMessage("");
      await fetchDetail();
    } catch (err) {
      // The answers may have moved on with a resubmission: read them again.
      if (bindsAnswers && isSubmissionSuperseded(err)) answers.reload();
      rethrow(err);
    }
  }

  const sla = payload?.sla;
  const investor = payload?.investor;
  // There is no by-id decisions endpoint, so the reviews ride along on the
  // approval payload the same way the context does.
  const reviews = Array.isArray(sla?.Reviews) ? sla.Reviews : [];

  const decidedBy = personName(
    sla?.DecidedBy || sla?.decided_by,
    sla?.decided_by_user_id
  );

  const headActions = (
    <Btn variant="ghost" size="sm" onClick={() => navigate("/compliance/decisions")}>
      <ChevronLeft /> Back to decisions
    </Btn>
  );

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title={sla ? `${sla.submission_type} · SLA #${sla.id}` : "Decision detail"}
        subtitle={sla && investor ? <span>{investorName(investor)}</span> : null}
        actions={headActions}
      />

      {error && <Alert tone="danger">{error}</Alert>}
      {flash && <Alert tone={flash.tone}>{flash.text}</Alert>}

      {!loading && sla && (
        <>
          <section className="card">
            <div className="card__head"><div className="card__title">Recorded decision</div></div>
            <div className="card__body">
              <KeyVal label="Decision" value={sla.decision || "-"} />
              <KeyVal label="Decided by" value={decidedBy} />
              <KeyVal
                label="Decided at"
                value={sla.decided_at ? new Date(sla.decided_at).toLocaleString() : "-"}
              />
              <KeyVal label="Review status" value={sla.review_status || "Unreviewed"} />
              {sla.reversal_of_id && (
                <KeyVal label="Reverses" value={`SLA #${sla.reversal_of_id}`} />
              )}
            </div>
          </section>

          <ApprovalContextCards payload={payload} />

          {bindsAnswers && (
            <SuitabilityAnswersCard
              answers={answers}
              item={slaRow}
              onOpenSuitabilityPage={() => navigate(`/suitability-overview/${slaRow.investment_id}`)}
            />
          )}

          <section className="card">
            <div className="card__head"><div className="card__title">Reviews</div></div>
            <div className="card__body">
              {reviews.length === 0 ? (
                <Empty>No reviews yet.</Empty>
              ) : (
                reviews.map((r, i) => (
                  <div
                    key={r.id || i}
                    style={{ padding: "6px 0", borderBottom: "1px solid var(--bd)" }}
                  >
                    <strong>{r.outcome || "-"}</strong>{" "}
                    <span className="muted">by {reviewerLabel(r)}</span>
                    <div className="mono" style={{ fontSize: "var(--t-xs)", color: "var(--tx-3)" }}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : "-"}
                    </div>
                    {r.notes && <div className="muted">{r.notes}</div>}
                  </div>
                ))
              )}
            </div>
          </section>

          {!canReview && (
            <Alert tone="info">
              Reviewing and reversing a decision are CCO or UDP actions. This
              session can read the record.
            </Alert>
          )}

          <section className="card">
            <div className="card__head"><div className="card__title">Review</div></div>
            <div className="card__body">
              <DecisionForm
                name="review"
                idPrefix="review"
                decisionOptions={REVIEW_OPTIONS}
                notesLabel="Review notes"
                submitLabel="Record review"
                confirmTitle="Confirm review"
                confirmBody="This records that you have examined the decision. It does not change the outcome."
                disabled={!canReview}
                onSubmit={submitReview}
              />
            </div>
          </section>

          <section className="card">
            <div className="card__head"><div className="card__title">Reverse</div></div>
            <div className="card__body">
              <DecisionForm
                name="reverse"
                idPrefix="reverse"
                decisionOptions={reverseOptions(sla.submission_type)}
                notesLabel="Reversal reason (mandatory, min 10 chars)"
                submitLabel="Reverse decision"
                confirmTitle="Confirm reversal"
                confirmBody="This overrides the original decision and notifies the investor. The reason is immutable."
                disabled={!canReview || !reversalBound}
                onSubmit={submitReverse}
              />
              <div className="field" style={{ marginTop: 12 }}>
                <label className="field__label" htmlFor="reverse-investor-message">
                  Message to the investor (optional)
                </label>
                <textarea
                  id="reverse-investor-message"
                  className="field__textarea"
                  rows={3}
                  value={reverseMessage}
                  disabled={!canReview}
                  onChange={(e) => setReverseMessage(e.target.value)}
                  placeholder="Included in the notification sent to the investor."
                />
              </div>
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}

/**
 * Remounted per decision, as InvestmentEdit is per order (InvestmentEditRoute):
 * a new slaId starts a new page, so no response, timer or callback of the
 * previous decision can reach it (Codex r14 P1).
 */
function ComplianceDecisionDetailRoute() {
  const { slaId } = useParams();
  return <ComplianceDecisionDetail key={slaId} />;
}

export default ComplianceDecisionDetailRoute;
